/*
 * Company settings. Each company stores only the values it changed (companies.settings); everything else comes from its
 * parents (HQ, then brand, then location), then from SETTINGS_DEFAULTS. resolveSettings does that in one place.
 *
 * Integration keys are never settings (they live encrypted in integration_keys).
 */
import { z } from "zod";
import { addressParts } from "./address.js";
import { KLARNA_MIN_CENTS } from "./billing.js";
import { EMPTY_PRICE_LIST, priceList } from "./prices.js";
import { PROPOSAL_SETTINGS_DEFAULTS, proposalSettings, proposalSettingsPatch } from "./proposals.js";

/**
 * A sub location of a location: a real branch with its own name, line, ZIPs, email and address (The Cleaners of Metairie
 * under The Cleaners of New Orleans), not just an area served. name is the place ("Metairie"); the brand name goes in
 * front of it where people read it.
 */
export const locationLine = z.object({
  name: z.string().trim().min(1).max(60),
  phone: z.string().regex(/^\+1\d{10}$/),
  zips: z.array(z.string().regex(/^\d{5}$/)).max(300),
  email: z.email().max(200).optional(),
  address: addressParts.optional(),
});
export type LocationLine = z.infer<typeof locationLine>;

/** "The Cleaners of Metairie" from the brand and the place, unless the name already says it in full. */
export function subLocationName(brand: string, name: string): string {
  return /^thes/i.test(name) ? name : `${brand} of ${name}`;
}

/** A Google Ads account id: 10 digits, dashes or not, or empty. */
const ADS_ID = /^(\d{3}-?\d{3}-?\d{4})?$/;

/** How the business is set up for taxes. */
export const ENTITY_KINDS = ["sole", "llc_single", "llc_multi", "s_corp", "c_corp", "partnership"] as const;
export const ENTITY_WORDS: Record<(typeof ENTITY_KINDS)[number], string> = {
  sole: "Just me, no LLC",
  llc_single: "LLC with one owner",
  llc_multi: "LLC with two or more owners",
  s_corp: "S corp (or an LLC taxed as one)",
  c_corp: "C corp",
  partnership: "Partnership",
};

/** Where and how the business files taxes, for the AI's tax notes and tax savings list. Empty means not told yet. */
export const taxProfile = z.object({
  /** Two letter state, like LA. */
  state: z.union([z.literal(""), z.string().regex(/^[A-Z]{2}$/)]),
  /** City and parish or county, like New Orleans, Orleans Parish. */
  city: z.string().trim().max(80),
  entity: z.union([z.literal(""), z.enum(ENTITY_KINDS)]),
  /** How the owner gets paid: draws, or a salary on payroll (S corp). */
  ownerPay: z.union([z.literal(""), z.enum(["draw", "salary"])]),
  /** The business is run from a room at home used only for work. */
  homeOffice: z.boolean(),
  /** How car and truck costs come off: miles at the IRS rate, or the real costs. */
  vehicle: z.union([z.literal(""), z.enum(["miles", "actual"])]),
  /** Anything else the AI should know (other income, a spouse on payroll, a big buy coming). */
  notes: z.string().trim().max(1000),
});
export type TaxProfile = z.infer<typeof taxProfile>;

const sections = {
  /** Hiring v2 (owner, 2026-11-04). */
  hiring: z.object({
    /** Text applicants: the thanks with a link to pick a phone screen time, reminders, close-outs. Off until the owner says. */
    texts: z.boolean(),
    /** Phone screen times every week, day 1 Monday to 7 Sunday at "HH:MM" in the company's time. */
    screenTimes: z
      .array(
        z
          .object({
            day: z.number().int().min(1).max(7),
            at: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
          })
          .strict(),
      )
      .max(80),
    /** How long a phone screen is. */
    screenMinutes: z.number().int().min(5).max(60),
    /** The roles applicants pick, each with its trial day pay per hour (owner: "trial is hourly and depends on role"). */
    roles: z
      .array(
        z
          .object({
            id: z.string().trim().min(1).max(30),
            name: z.string().trim().min(1).max(60),
            nameEs: z.string().trim().max(60),
            trialRateCents: z.number().int().min(0).max(100_000),
          })
          .strict(),
      )
      .max(12),
    /** Paid to the crew member who sent a hire, after referralAfterDays. 0 is off (owner: "no referral bonus rn"). */
    referralBonusCents: z.number().int().min(0).max(100_000),
    referralAfterDays: z.number().int().min(1).max(365),
  }),
  messaging: z.object({
    /** Real texts and emails go out only when this is on. This build keeps it off for every company. */
    live: z.boolean(),
    /** Automatic reminders send by themselves only when this is on. */
    autoReminders: z.boolean(),
    /**
     * More business lines whose texts and calls come to this company, in +1 form (a sublocation's line, a toll-free
     * number). Its own phone always does.
     */
    otherLines: z.array(z.string().regex(/^\+1\d{10}$/)).max(10),
    /**
     * A sublocation's own line and ZIPs (old Steward locations: textFrom and zips). Automatic texts to a client whose ZIP
     * is on the list go from that line (outbox/lines.ts). The line must be in otherLines too, so its texts come here.
     */
    locationLines: z.array(locationLine).max(10),
  }),
  invoices: z.object({
    subject: z.string().max(200),
    text: z.string().max(2000),
    /** The invoice email's words above the pay box (old Steward emailTemplate). Empty uses the trade's own words. */
    email: z.string().max(3000),
    tips: z.boolean(),
    /** The three tip buttons on the pay page, in percent. */
    tipPercents: z.array(z.number().int().min(1).max(50)).length(3),
    payWays: z.object({ card: z.boolean(), bank: z.boolean(), klarna: z.boolean() }),
    /** Past due after this many hours for homes. */
    pastDueHoursHome: z
      .number()
      .int()
      .min(0)
      .max(24 * 60),
    /** Show the visit's photos on the client's pay page (owner, question 37: proof for mowing, off for cleaning). */
    photosOnPayPage: z.boolean(),
    /** Past due after this many days for businesses (a client with a company name). */
    pastDueDaysBusiness: z.number().int().min(0).max(120),
    /**
     * When a finished visit's saved card is charged by itself (the old autobill): now, an hour later, or at 6 PM that day,
     * so the office can fix the price or press Hold first.
     */
    autoChargeWhen: z.enum(["now", "hour", "evening"]),
    /**
     * Charges after each visit (owner, 2026-11-02): on, saving or paying with a card under the Card terms turns them on for
     * the client (Cleaners); ask, the pay page asks with a box first (GroundsMen).
     */
    autoChargeOnSave: z.enum(["on", "ask"]),
    /** How to pay by cash or check, shown on the pay page and the PDF (old Steward "Payment instructions"). */
    payNote: z.string().max(500),
    /** Small print at the bottom of every invoice and its PDF (old Steward "Contract / Disclaimer"). */
    footer: z.string().max(1500),
    /** Klarna shows only when the balance is at least this, in cents (old Steward klarna.min, $300). */
    klarnaMinCents: z.number().int().min(3_500).max(1_000_000),
    /** The office may bill part of a one-time job early on its own invoice (old Steward progressPay). */
    progress: z.boolean(),
  }),
  payroll: z.object({
    /** 1 Monday to 7 Sunday. The week (Monday to Sunday) is paid on this day the week after. */
    paydayWeekday: z.number().int().min(1).max(7),
    /** The pool percent used when a visit has no higher one; null means none. */
    defaultPoolPercent: z.number().min(0).max(100).nullable(),
    /** The set pool on visits for a business client, whatever the crew's own percents; null means none (owner, 2026-11-02). */
    commercialPoolPercent: z.number().min(0).max(100).nullable(),
    /** 1: pay every week (the default). 2: pay every two weeks (owner, question 47). */
    periodWeeks: z.union([z.literal(1), z.literal(2)]),
    /** With two-week pay: the Monday the first two-week period starts. Weeks before it stay weekly. Null: Jan 5 2026. */
    twoWeekFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable(),
  }),
  crew: z.object({
    /** Crew may take cash or check from clients and say so when they complete a visit. */
    collectsMoney: z.boolean(),
  }),
  brand: z.object({
    /** Links at the bottom of the pay and quote pages (old Steward "Social networks"). Empty ones are left out. */
    links: z.object({
      facebook: z.string().max(300),
      instagram: z.string().max(300),
      google: z.string().max(300),
      yelp: z.string().max(300),
      nextdoor: z.string().max(300),
      angi: z.string().max(300),
      bbb: z.string().max(300),
    }),
  }),
  schedule: z.object({
    /**
     * The arrival window told to clients when a job has none of its own: this many minutes before and after the start
     * (old Steward "Arrival window"). Both 0: clients hear the start time.
     */
    arriveBefore: z.number().int().min(0).max(240),
    arriveAfter: z.number().int().min(0).max(240),
    /** Color visits on the Schedule by a word in the job's name (old Steward "Calendar colors"). First match wins. */
    colors: z.array(z.object({ word: z.string().trim().min(1).max(40), color: z.string().regex(/^#[0-9a-fA-F]{6}$/) })).max(30),
  }),
  alerts: z.object({
    /** The bell tells the office about these (old Steward "Notifications"). Crew alerts about their own jobs always go. */
    newRequest: z.boolean(),
    quoteApproved: z.boolean(),
    /** The first time a client opens a sent quote, so someone calls while it is fresh. */
    quoteOpened: z.boolean(),
    paid: z.boolean(),
    /** The owner's Paid invoice email, the first time each invoice turns paid (old Steward Notifications, Paid). */
    paidEmail: z.boolean(),
    /** The owner's email about each new website request, beside the text (old Steward request alert email). */
    leadEmail: z.boolean(),
    /** Where those emails go. Empty: the company's email. */
    emailTo: z.union([z.literal(""), z.email().max(200)]),
    /** A phone texted about each new website request, to call back fast (old Steward requestAlert). Empty: none. */
    textTo: z.string().max(20),
  }),
  leads: z.object({
    /** Company ids this company shares leads with. Never automatic. */
    shareWith: z.array(z.uuid()),
  }),
  /** The price list the auto quoter prices from (prices.ts). A brand sets it once; locations get it unless they change it. */
  prices: priceList,
  /** How commercial proposals are priced and what they say (proposals.ts). */
  proposals: proposalSettings,
  website: z.object({
    /** The brand websites allowed to send requests, like https://thecleanersusa.com. Empty: the website routes are closed. */
    origins: z.array(z.url().max(200)).max(10),
  }),
  capacity: z.object({
    /** Count teams per day and close full days. Never on for route businesses (lawn). */
    on: z.boolean(),
    /** Teams working each weekday, Monday first. Teams of teamSize people. */
    teamsByWeekday: z.array(z.number().int().min(0).max(50)).length(7),
    teamSize: z.number().int().min(1).max(6),
    /** Hours each person works a day. */
    hoursEach: z.number().min(1).max(14),
    /** Hours a job takes when it has none typed. */
    defaultJobHours: z.number().min(0.5).max(14),
  }),
  field: z.object({
    /** Where the teams start the day, for Best order. */
    startAddress: z.string().max(300),
    /** The arrival guess in the On my way text when the drive time is not known. */
    onMyWayMinutes: z.number().int().min(5).max(120),
    /**
     * Clock out a person on their own at the time their last visit of the day is marked done (old tracking.php; on by
     * default since 2026-10-30). Off: they clock out themselves, and a forgotten one shows in Loose ends.
     */
    autoClockOut: z.boolean(),
    /**
     * Live GPS: while someone is on the clock their phone sends where it is every few minutes, for the green pins on the
     * Home map. On by default (owner, 2026-11-01); HQ, a brand or a location can turn it off. Off: nothing is kept.
     */
    liveGps: z.boolean(),
  }),
  routes: z.object({
    /**
     * Named routes inside the Schedule: a Route view with stops in order, a map, Best order, and route chips on the month.
     * Null follows the trade (on for lawn, off for the rest); the owner can turn it on or off in Settings, Schedule.
     */
    on: z.boolean().nullable(),
  }),
  booking: z.object({
    /** A quote priced by the auto quoter books itself: the client picks a day on the quote page and it goes on the schedule. */
    fromQuote: z.boolean(),
    /** A card must be saved to hold the day. Null follows the trade (on for cleaning, off for the rest). */
    cardFirst: z.boolean().nullable(),
  }),
  /** What a client must do to approve a quote (like Jobber): save a card, and sign with a finger or mouse. */
  quoteApproval: z.object({
    card: z.boolean(),
    sign: z.boolean(),
  }),
  /**
   * The policies a client agrees to when they sign a quote or a job confirm (Settings, Quotes). The exact words are saved
   * with each signature, so a dispute file shows what that client saw and signed. An empty one is not shown.
   */
  policies: z.object({
    cancellation: z.string().max(3000),
    guarantee: z.string().max(3000),
    payment: z.string().max(3000),
    /** Hours after a visit a client has to tell us something is wrong (the guarantee window). The dispute file checks it. */
    complaintHours: z.number().int().min(1).max(720),
  }),
  receptionist: z.object({
    /** off, practice (he writes replies down, nothing is sent) or live. A paid add-on. */
    mode: z.enum(["off", "practice", "live"]),
    /** His name. Eunice is a he. */
    name: z.string().min(1).max(30),
    /** What he knows about the brand: services, areas, hours, how quotes work. The owner writes it. */
    brief: z.string().max(6000),
    /** The price list in words, when he may quote. Empty: he never gives a price. */
    pricing: z.string().max(6000),
    careersUrl: z.string().max(300),
    quoteUrl: z.string().max(300),
    /** The quote page prices on the spot ("in 2 minutes"). */
    instantQuote: z.boolean(),
    /** The trade's word for the work in the missed call nudge (cleaning, lawn care, pressure washing). */
    serviceWord: z.string().max(40),
    /** Text back missed calls. */
    missedText: z.boolean(),
    /** A new lead who goes quiet a day after our question gets one short check in (receptionist/noreply.ts). */
    noReplyNudge: z.boolean(),
    /** Minutes he waits after a text so the owner or team can answer first. Anyone of ours texting back in that time means
     * he stays out (owner, 2026-11-02). 0 answers right away. */
    waitMinutes: z.number().int().min(0).max(120),
    /** Replies a month included in the add-on. Past it he hands every text to the office. */
    monthlyAllowance: z.number().int().min(0).max(100_000),
    /** The owner's own phones: texts from them are instructions, never answered as a client. */
    ownerPhones: z.array(z.string().max(20)).max(10),
  }),
  marketing: z.object({
    /** The Google review link sent in review requests. Empty: review requests stay off. */
    reviewUrl: z.string().max(300),
    /** What a referral is worth to both people, in cents. */
    referralCents: z.number().int().min(0).max(100_000),
    /** Posting to Google and Facebook (replies, profile posts, social posts) has its own switch, apart from texts and
     * emails (owner answer 111). Off until the owner turns it on. */
    postingLive: z.boolean(),
    /** New 4 and 5 star reviews get a reply on their own (owner answer 110). Lower ratings wait for the office. */
    autoReply: z.boolean(),
    /** The Google account the Steward acts as for the Business Profile: a Workspace address that manages the profile,
     * reached through the service account's domain-wide delegation. Set at HQ; brands get it unless they set their own. */
    googleUser: z.union([z.literal(""), z.email().max(120)]),
    /** The Google Ads account (123-456-7890) whose spend is read. Local Services spend comes from the same account. */
    googleAdsId: z.string().trim().regex(ADS_ID, "Type the 10 digit Google Ads id, like 123-456-7890."),
    /** The manager account the service account was added to, when the ads account sits under one. */
    googleAdsManagerId: z.string().trim().regex(ADS_ID, "Type the 10 digit manager id, like 123-456-7890."),
    /** The Meta ad account whose spend is read (the digits after act_). */
    metaAdAccountId: z
      .string()
      .trim()
      .regex(/^((act_)?\d{5,25})?$/, "Type the ad account id, the digits after act_."),
    /** The Facebook Page posts go to. Instagram posts go to the Instagram account linked to this Page. */
    facebookPageId: z
      .string()
      .trim()
      .regex(/^(\d{5,25})?$/, "Type the Page id, digits only."),
    /** More Google Ads accounts picked after the first (owner, 2026-11-02: "allow multiple to be clicked"). Spend adds up. */
    googleAdsMore: z
      .array(
        z.object({
          id: z
            .string()
            .trim()
            .regex(/^\d{3}-?\d{3}-?\d{4}$/, "Type the 10 digit Google Ads id."),
          managerId: z.string().trim().regex(ADS_ID, "Type the 10 digit manager id."),
        }),
      )
      .max(20),
    /** More Meta ad accounts after the first. Spend adds up. */
    metaAdAccountsMore: z.array(z.string().regex(/^\d{5,25}$/, "Type the ad account id.")).max(20),
    /** More Facebook Pages after the first. Each post goes to every Page (and the Instagram linked to it). */
    facebookPagesMore: z.array(z.string().regex(/^\d{5,25}$/, "Type the Page id.")).max(20),
  }),
  /** Books (owner, 2026-11-01: "im still doing everything"): how much the AI does by itself, and where taxes are filed. */
  books: z.object({
    /** The AI posts the lines it is sure of and pairs clear transfers. The rest wait for a person. */
    aiPosts: z.boolean(),
    /** The AI goes over new bank lines by itself after each nightly bank pull. */
    aiAfterSync: z.boolean(),
    /** Each month of a linked account is checked against the bank and reconciled when it matches; a month off rings the bell. */
    autoReconcile: z.boolean(),
    /** The first day in Books (YYYY-MM-DD). Bank lines before it are left out. Empty for everything the bank sends. */
    startDay: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, "Pick a day."),
    tax: taxProfile,
  }),
};

export const settingsSchema = z.object(sections).strict();
export type Settings = z.infer<typeof settingsSchema>;

/** What a company may store: any part of any section. Unknown names are refused. */
export const settingsPatchSchema = z
  .object({
    messaging: sections.messaging.partial().strict().optional(),
    invoices: sections.invoices
      .extend({ payWays: sections.invoices.shape.payWays.partial().strict() })
      .partial()
      .strict()
      .optional(),
    hiring: sections.hiring.partial().strict().optional(),
    payroll: sections.payroll.partial().strict().optional(),
    crew: sections.crew.partial().strict().optional(),
    brand: sections.brand.extend({ links: sections.brand.shape.links.partial().strict() }).partial().strict().optional(),
    schedule: sections.schedule.partial().strict().optional(),
    alerts: sections.alerts.partial().strict().optional(),
    leads: sections.leads.partial().strict().optional(),
    prices: sections.prices.partial().strict().optional(),
    proposals: proposalSettingsPatch.optional(),
    website: sections.website.partial().strict().optional(),
    marketing: sections.marketing.partial().strict().optional(),
    receptionist: sections.receptionist.partial().strict().optional(),
    capacity: sections.capacity.partial().strict().optional(),
    field: sections.field.partial().strict().optional(),
    routes: sections.routes.partial().strict().optional(),
    booking: sections.booking.partial().strict().optional(),
    quoteApproval: sections.quoteApproval.partial().strict().optional(),
    policies: sections.policies.partial().strict().optional(),
    books: sections.books.extend({ tax: taxProfile.partial().strict() }).partial().strict().optional(),
  })
  .strict();
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;

export const SETTINGS_DEFAULTS: Settings = {
  hiring: {
    texts: false,
    screenTimes: [1, 2, 3, 4, 5].flatMap((day) => [
      { day, at: "10:00" },
      { day, at: "14:00" },
    ]),
    screenMinutes: 15,
    roles: [],
    referralBonusCents: 0,
    referralAfterDays: 60,
  },
  messaging: { live: false, autoReminders: false, otherLines: [], locationLines: [] },
  invoices: {
    subject: "Your invoice from {brand}",
    text: "Hi {first}, here's your {job} invoice.",
    email: "",
    tips: true,
    tipPercents: [10, 15, 20],
    // Bank on by default (owner, 2026-10-30: "why isn't there bank on invoices"); Klarna stays a choice.
    payWays: { card: true, bank: true, klarna: false },
    pastDueHoursHome: 24,
    photosOnPayPage: false,
    pastDueDaysBusiness: 7,
    autoChargeWhen: "now",
    autoChargeOnSave: "on",
    payNote: "",
    footer: "",
    klarnaMinCents: KLARNA_MIN_CENTS,
    progress: true,
  },
  payroll: { paydayWeekday: 5, defaultPoolPercent: null, commercialPoolPercent: 50, periodWeeks: 1, twoWeekFrom: null },
  crew: { collectsMoney: false },
  brand: { links: { facebook: "", instagram: "", google: "", yelp: "", nextdoor: "", angi: "", bbb: "" } },
  schedule: { arriveBefore: 0, arriveAfter: 0, colors: [] },
  alerts: {
    newRequest: true,
    quoteApproved: true,
    quoteOpened: true,
    paid: true,
    paidEmail: true,
    leadEmail: true,
    emailTo: "",
    textTo: "",
  },
  leads: { shareWith: [] },
  prices: EMPTY_PRICE_LIST,
  proposals: PROPOSAL_SETTINGS_DEFAULTS,
  website: { origins: [] },
  marketing: {
    reviewUrl: "",
    referralCents: 2500,
    postingLive: false,
    autoReply: true,
    googleUser: "",
    googleAdsId: "",
    googleAdsManagerId: "",
    metaAdAccountId: "",
    facebookPageId: "",
    googleAdsMore: [],
    metaAdAccountsMore: [],
    facebookPagesMore: [],
  },
  receptionist: {
    mode: "off",
    name: "Eunice",
    brief: "",
    pricing: "",
    careersUrl: "",
    quoteUrl: "",
    instantQuote: false,
    serviceWord: "cleaning",
    missedText: false,
    noReplyNudge: true,
    waitMinutes: 5,
    monthlyAllowance: 500,
    ownerPhones: [],
  },
  capacity: { on: false, teamsByWeekday: [2, 2, 2, 2, 2, 0, 0], teamSize: 2, hoursEach: 8, defaultJobHours: 3 },
  field: { startAddress: "", onMyWayMinutes: 20, autoClockOut: true, liveGps: true },
  routes: { on: null },
  booking: { fromQuote: true, cardFirst: null },
  quoteApproval: { card: true, sign: true },
  // Owner, 2026-10-31: every brand's guarantee is 24 hours. The cancel fee differs by brand, so each brand writes its own.
  policies: {
    cancellation:
      "Need to cancel or move a visit? Tell us at least 24 hours before it starts. If you cancel less than 24 hours before, a late cancel fee may be charged to your card on file.",
    guarantee:
      "If something is not right, tell us within 24 hours of your visit and we will come back and fix it for free. Photos help. We can not redo or refund work we were not told about within 24 hours. Our fix is a free return visit, not a refund.",
    payment:
      "You OK us to charge the card you save for each visit once it is done, and for any late cancel fee. You get a receipt each time. Questions about a charge? Text or call us first and we will make it right.",
    complaintHours: 24,
  },
  books: {
    aiPosts: true,
    aiAfterSync: true,
    autoReconcile: true,
    startDay: "",
    tax: { state: "", city: "", entity: "", ownerPay: "", homeOffice: false, vehicle: "", notes: "" },
  },
};

/** The policies a client signs, in order, with the names clients see. */
export const POLICY_NAMES = [
  ["cancellation", "Cancellation policy"],
  ["guarantee", "Our guarantee"],
  ["payment", "Payment policy"],
] as const satisfies readonly (readonly [Exclude<keyof Settings["policies"], "complaintHours">, string])[];

/** One policy as the client saw it when they signed. */
export interface SignedPolicy {
  key: string;
  title: string;
  text: string;
}

/** The policies to show above the signature box and save with the signature. Empty ones are left out. */
export function policiesToSign(s: Pick<Settings, "policies">): SignedPolicy[] {
  return POLICY_NAMES.flatMap(([key, title]) => {
    const text = s.policies[key].trim();
    return text ? [{ key, title, text }] : [];
  });
}

/** The footer links a brand may show, with the name clients see. */
export const BRAND_LINKS = [
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["google", "Google"],
  ["yelp", "Yelp"],
  ["nextdoor", "Nextdoor"],
  ["angi", "Angi"],
  ["bbb", "BBB"],
] as const satisfies readonly (readonly [keyof Settings["brand"]["links"], string])[];

/** The brand's footer links that are filled in, with their names. */
export function brandLinks(s: Pick<Settings, "brand">): { label: string; url: string }[] {
  return BRAND_LINKS.flatMap(([k, label]) => {
    const url = s.brand.links[k].trim();
    return url ? [{ label, url }] : [];
  });
}

/**
 * The arrival window clients hear, as [from, to] in HH:MM, or [start] alone: the job's own window first, else the
 * company's minutes around the start time, else the start time. Null with no time at all.
 */
export function arrivalTimes(
  job: { arriveFrom: string | null; arriveTo: string | null; startTime: string | null },
  s: Pick<Settings, "schedule">,
): [string] | [string, string] | null {
  const from = job.arriveFrom?.slice(0, 5);
  const to = job.arriveTo?.slice(0, 5);
  if (from && to && to > from) return [from, to];
  const start = job.startTime?.slice(0, 5);
  if (!start) return null;
  const { arriveBefore, arriveAfter } = s.schedule;
  if (!arriveBefore && !arriveAfter) return [start];
  const [h, m] = start.split(":").map(Number) as [number, number];
  const at = (mins: number) => {
    const t = Math.min(Math.max(h * 60 + m + mins, 0), 23 * 60 + 59);
    return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  };
  return [at(-arriveBefore), at(arriveAfter)];
}

/** A visit's color on the Schedule from the company's word colors (first match wins), or null. */
export function scheduleColor(name: string, s: Pick<Settings, "schedule">): string | null {
  const lower = name.toLowerCase();
  return s.schedule.colors.find((c) => lower.includes(c.word.toLowerCase()))?.color ?? null;
}

/** Routes are on when the owner turned them on, or by the trade when they never chose (lawn runs on routes). */
export function routesOn(s: Pick<Settings, "routes">, trade: string): boolean {
  return s.routes.on ?? trade === "lawn";
}

/** A card is needed to book from a quote when the owner said so, or by the trade when they never chose (cleaning holds the day with a card). */
export function cardToBook(s: Pick<Settings, "booking">, trade: string): boolean {
  return s.booking.cardFirst ?? trade === "cleaning";
}

/** A card must be saved before a quote can be approved: the company rule (on unless the owner turned it off) or the quote's own ask. */
export function cardToApprove(s: Pick<Settings, "quoteApproval">, quoteAsks: boolean): boolean {
  return s.quoteApproval.card || quoteAsks;
}

type Plain = Record<string, unknown>;
const isPlain = (v: unknown): v is Plain => typeof v === "object" && v !== null && !Array.isArray(v);

/** Lays b over a: objects merge key by key, anything else (numbers, lists, null) replaces. */
export function mergeSettings(a: Plain, b: Plain): Plain {
  const out: Plain = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (v === undefined) continue;
    const prev = out[k];
    out[k] = isPlain(prev) && isPlain(v) ? mergeSettings(prev, v) : v;
  }
  return out;
}

/**
 * The settings a company works with. chain is each company's own stored values from the top (HQ) down to the company
 * itself; later ones win. A stored value that no longer fits the schema is ignored rather than breaking the company.
 */
export function resolveSettings(chain: readonly unknown[]): Settings {
  let merged: Plain = { ...SETTINGS_DEFAULTS };
  for (const stored of chain) {
    const parsed = settingsPatchSchema.safeParse(stored ?? {});
    if (parsed.success) merged = mergeSettings(merged, parsed.data);
  }
  const s = settingsSchema.parse(merged);
  // Auto scheduling needs capacity (owner, 2026-10-30): clients only see open days and times when full ones close.
  return s.booking.fromQuote ? { ...s, capacity: { ...s.capacity, on: true } } : s;
}
